import { PartyType, PaymentFrequency, PhonePriority, PhoneType, ProductType } from "@app/modules/policySpace/common/policyConstants";

import { SortCriteriaDirection } from "@app/models/constants";
import { PolicySearchColumnNameHeader, PolicySearchType, SearchPolicySortBy } from "@app/modules/policySpace/common/constants";
import {
    PeriodType,
    PolicyPartyType,
    PolicySearchBusinessType,
    PolicySearchFormFields,
    PolicySearchOwnerType,
} from "@app/modules/policySpace/policySearch/policySearchConstants";
import { RouteIdentityState } from "@app/routes/models";

// Policy Search Request
export interface PolicySearchRequest {
    searchCriterias: PolicySearchCriterias;
}

export type PolicySearchCriterias = Partial<SearchCriterias>;

export const getSearchCriteria = (searchCriteria: PolicySearchCriterias): PolicySearchCriterias => {
    const copySearchCriterias: PolicySearchCriterias = { ...searchCriteria };
    copySearchCriterias.policyNumber = copySearchCriterias.policyNumber?.trim();
    copySearchCriterias.applicationNumber = copySearchCriterias.applicationNumber?.trim();

    return copySearchCriterias;
};

export interface SearchCriterias {
    policyNumber: string;
    applicationNumber: string;
    insuredLastName: string;
    insuredFirstName: string;
    insuredDateOfBirth: string;
    insuredPhone: string;
    insuredAdditionalFields: string[];
    ownerLastName: string;
    ownerFirstName: string;
    ownerCompanyName: string;
    ownerDateOfBirth: string;
    ownerPhone: string;
    ownerAdditionalFields: string[];
    agentPartyID: string;
    policyStatus: string;
    isIssued: boolean;
    isRefused: boolean;
    isTerminated: boolean;
    policyGroup: string;
    productType: string;
    productShortName: string;
    startDate: string;
    endDate: string;
    agentID: string;
    agentContractNumber: string;
    sortBy: SearchPolicySortBy;
    orderBy: SortCriteriaDirection;
    limit: number;
    offset: number;
}

export interface PolicyAdditionalInformation {
    type: string;
    valueType: string;
    value: string;
}

// Policy Search Response
export interface PolicySearchResponse {
    policies: PolicyResponse[];
    totalMatchedRecords: number;
}

export interface PolicyResponse {
    policy: PolicySearchResult;
    parties: PolicySearchParty[];
    additionalInformation: PolicyAdditionalInformation[];
}

export interface PolicySearchResult {
    policyNumber: string;
    applicationNumber: string;
    status: string;
    isIssued: boolean;
    isRefused: boolean;
    isTerminated: boolean;
    productShortName: string;
    submissionDate: string;
    issueDate: string;
    effectiveDate: string;
    terminationDate: string;
    paymentDate: string;
    dueDate: string;
    yearlyPremium: number;
    modalPremium: number;
    inGraceStatus: boolean;
    enteredGraceDate: string;
    totalBaseCoverageFaceAmount: number;
    totalPrimaryInsuredFaceAmount: number;
    totalGracePremiumDue: number;
    paymentFrequency: PaymentFrequency;
    productType: ProductType;
    owners: PolicySearchOwner[];
    insureds: PolicySearchInsured[];
    agents: PolicySearchAgent[];
    currencyCode: string;
    subType: string;
}

export interface PolicySearchParty {
    partyID: string;
    partyType: PartyType;
    firstName: string;
    lastName: string;
    companyName: string;
    dateOfBirth: string;
    email: string;
    addresses: PolicySearchAddress[];
    phones: PolicySearchPhone[];
}

export interface PolicySearchOwner {
    partyID: string;
    ownershipPercentage: number;
    userID: string;
    portalAccess: boolean;
    party?: PolicySearchParty;
}

export interface PolicySearchInsured {
    partyID: string;
    issueAge: number;
}

export interface PolicySearchAgent {
    partyID: string;
}

interface PolicySearchAddress {
    addressID: string;
    city: string;
    state: string; // should be StateEnum, ISO 3166-2 list of states/provinces/subdivisions
    country: string; // should be CountryEnum, See ISO 3166-1 list of countries
    zipCode: string;
}

export interface PolicySearchPhone {
    phoneID: string;
    prioritySequence: PhonePriority;
    phoneType: PhoneType;
    phoneNumber: string;
    extension: string;
    countryCode: string;
}

export interface PolicySortCriteria {
    columnName: string;
    direction: SortCriteriaDirection;
}

export type PolicySearchFormValues = {
    policyNumber: string;
    applicationNumber: string;
    insuredLastName: string;
    insuredFirstName: string;
    insuredDateOfBirth: string;
    insuredPhone: string;
    insuredIDNumber: string;
    insuredCIFNumber: string;
    ownerLastName: string;
    ownerFirstName: string;
    ownerCompanyName: string;
    ownerDateOfBirth: string;
    ownerPhone: string;
    ownerIDNumber: string;
    ownerCIFNumber: string;
    typeOfOwner: string;
    productType: string;
    productShortName: string;
    startDate: string;
    endDate: string;
    periodType: PeriodType;
    agentID: string;
    agentContractNumber: string;
    policyNumberOnly: boolean;
};

export type PolicySearchFormValidations = {
    atLeastOneFieldIsPresent: boolean;
    policyNumber: boolean;
    applicationNumber: boolean;
    insuredLastName: boolean;
    insuredFirstName: boolean;
    insuredDateOfBirth: boolean;
    insuredPhone: boolean;
    insuredIDNumber: boolean;
    insuredCIFNumber: boolean;
    ownerLastName: boolean;
    ownerFirstName: boolean;
    ownerCompanyName: boolean;
    ownerDateOfBirth: boolean;
    ownerPhone: boolean;
    ownerIDNumber: boolean;
    ownerCIFNumber: boolean;
    typeOfOwner: boolean;
    productType: boolean;
    productShortName: boolean;
    startDate: boolean;
    endDate: boolean;
    agentID: boolean;
    agentContractNumber: boolean;
};

export type PolicySearchPlan = {
    type: string;
    key: string;
    value: string;
};

export type SearchPolicyLocationState = {
    from: string;
    policyIdentifier?: string;
    productType?: ProductType;
    searchCriteria: PolicySearchCriterias;
    advancedSearchType: PolicySearchType;
    advancedFormValues: PolicySearchFormValues;
    sortColumnName?: PolicySearchColumnNameHeader;
    sortDirection?: SortCriteriaDirection;
    isSubmitting: boolean;
    pageIndex?: number;
    partyType: PolicyPartyType;
    isFrom?: RouteIdentityState;
};
export const initialFormValues: PolicySearchFormValues = {
    [PolicySearchFormFields.PolicyNumber]: "",
    [PolicySearchFormFields.ApplicationNumber]: "",
    [PolicySearchFormFields.InsuredLastName]: "",
    [PolicySearchFormFields.InsuredFirstName]: "",
    [PolicySearchFormFields.InsuredDateOfBirth]: "",
    [PolicySearchFormFields.InsuredPhone]: "",
    [PolicySearchFormFields.InsuredIDNumber]: "",
    [PolicySearchFormFields.InsuredCIFNumber]: "",
    [PolicySearchFormFields.OwnerLastName]: "",
    [PolicySearchFormFields.OwnerFirstName]: "",
    [PolicySearchFormFields.OwnerCompanyName]: "",
    [PolicySearchFormFields.OwnerDateOfBirth]: "",
    [PolicySearchFormFields.OwnerPhone]: "",
    [PolicySearchFormFields.OwnerIDNumber]: "",
    [PolicySearchFormFields.OwnerCIFNumber]: "",
    [PolicySearchFormFields.TypeOfOwner]: PolicySearchOwnerType.Individual,
    [PolicySearchFormFields.TypeOfBusiness]: PolicySearchBusinessType.All,
    [PolicySearchFormFields.ProductShortName]: "",
    [PolicySearchFormFields.StartDate]: "",
    [PolicySearchFormFields.EndDate]: "",
    [PolicySearchFormFields.PeriodType]: PeriodType.None,
    [PolicySearchFormFields.AgentID]: "",
    [PolicySearchFormFields.AgentContractNumber]: "",
    [PolicySearchFormFields.PolicyNumberOnly]: true,
};

export const initialValidations: PolicySearchFormValidations = {
    [PolicySearchFormFields.PolicyNumber]: true,
    [PolicySearchFormFields.ApplicationNumber]: true,
    [PolicySearchFormFields.OwnerFirstName]: true,
    [PolicySearchFormFields.OwnerLastName]: true,
    [PolicySearchFormFields.InsuredFirstName]: true,
    [PolicySearchFormFields.InsuredLastName]: true,
    [PolicySearchFormFields.OwnerDateOfBirth]: true,
    [PolicySearchFormFields.InsuredDateOfBirth]: true,
    [PolicySearchFormFields.OwnerPhone]: true,
    [PolicySearchFormFields.OwnerIDNumber]: true,
    [PolicySearchFormFields.OwnerCIFNumber]: true,
    [PolicySearchFormFields.InsuredPhone]: true,
    [PolicySearchFormFields.InsuredIDNumber]: true,
    [PolicySearchFormFields.InsuredCIFNumber]: true,
    [PolicySearchFormFields.OwnerCompanyName]: true,
    [PolicySearchFormFields.TypeOfOwner]: true,
    [PolicySearchFormFields.TypeOfBusiness]: true,
    [PolicySearchFormFields.ProductShortName]: true,
    [PolicySearchFormFields.StartDate]: true,
    [PolicySearchFormFields.EndDate]: true,
    [PolicySearchFormFields.AgentID]: true,
    [PolicySearchFormFields.AgentContractNumber]: true,
    atLeastOneFieldIsPresent: false,
};

export type PolicySearchState = {
    searchCriteria?: PolicySearchCriterias;
    sortColumnName?: PolicySearchColumnNameHeader;
    sortDirection?: SortCriteriaDirection;
    advancedSearchType?: PolicySearchType;
    advancedFormValues?: PolicySearchFormValues;
    validations?: PolicySearchFormValidations;
    partyType?: PolicyPartyType;
    page: number;
};

export const initialSearchType = PolicySearchType.PolicyNumber;
export const initialPartyType = PolicyPartyType.None;
export const defaultPolicySearchState: PolicySearchState = {
    searchCriteria: {},
    advancedSearchType: initialSearchType,
    advancedFormValues: initialFormValues,
    validations: initialValidations,
    sortColumnName: PolicySearchColumnNameHeader.Status,
    sortDirection: SortCriteriaDirection.Asc,
    partyType: initialPartyType,
    page: 1,
};

export const defaultPolicySearchConfig: PolicySearchDefinition = {
    enableAdditionalFields: false,
    policySearchResult: {
        maxAdditionalInfoMobile: 3,
        maxAdditionalInfoDesktop: 5,
        cardHeaderItemNbMobile: 4,
        mobileOwnerNb: 1,
        hideUnissuedPolicyNumber: false,
    },
    typesOfBusiness: ["life", "annuity"],
    issueDate: {
        visible: true,
    },
};
export interface PolicyPlanResponse {
    policyPlans: PolicyPlan[];
}

export interface PolicyPlan {
    name: string;
    type: ProductType;
}

export type KeyValueType = {
    key: string;
    value: string;
};

export type KeyValueTypeWithFeatureFlag = {
    key: string;
    value: string;
    featureFlag: string;
};

export interface SearchPolicyPartyType {
    partyType: PolicyPartyType;
    label: string;
}

export interface Period {
    start: string;
    end: string;
    periodType: PeriodType;
}

export interface SearchPeriod {
    type: PeriodType;
    label: string;
}
